import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges } from '@angular/core';
import { GroupDescriptor, GroupResult, groupBy } from '@progress/kendo-data-query';
import { PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { BehaviorSubject, Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';
import { FeatureGroup, FeatureOption, RawFeature, PatchORDBitDto, OrderFeatureAssociation } from '../../interfaces/request.interface';
import { OptionState } from '../../store/state/options.state';
import { CombinedSelectors } from '../../store/state/combined.selectors';
import { Store } from '@ngxs/store';
import { RequestActions } from '../../store/action/request.action';
import { RequestState, RequestStateModel } from '../../store/state/request.state';

@Component({
    selector: 'app-bitfeature-data',
    templateUrl: './bitfeature-data.component.html',
    styleUrl: './bitfeature-data.component.css',
    standalone: false
})
export class BitfeatureDataComponent implements OnChanges {
  @Input() draftId?: number;
  @Input() readonly: boolean = false;
  @Input() set preselectedIds(ids: Array<string | number> | null | undefined) {
    const values = Array.isArray(ids) ? ids : [];
    if(!this.run)
    {this.applyIncomingSelection(values); this.run = true;}
  }
  @Output() selectionChange = new EventEmitter<string[]>();
 public run = false;
  public groups: GroupDescriptor[] = [{ field: 'subcat', dir: 'asc' }];
  public groupsVM: FeatureGroup[] = [];
  public selectedGroupsVm: FeatureGroup[] = [];
  public selectedIdsSet: Set<string> = new Set<string>();
  public searchTerm = '';
  public expandMode = PanelBarExpandMode.Single;
  public hasOptions$!: Observable<boolean>;

  private destroy$ = new Subject<void>();
  private rawData$!: Observable<RawFeature[] | GroupResult[]>;
  private activeSubPSL$!: Observable<string>;
  private searchTerm$ = new BehaviorSubject<string>('');
  private optionLookup = new Map<string, RawFeature>();
  private optionGroupLookup = new Map<string, string>();
  private syncingFromStore = false;

  titleLeft = 'Available Features';
  titleRight = 'Selected Features';
  disableClear = false;

  constructor(private store: Store) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle readonly state changes - disable clear button and interactions
    if (changes['readonly']) {
      this.disableClear = this.readonly;
    }
  }

  ngOnInit(): void {
    this.rawData$ = this.store.select(OptionState.bitFeature);
    this.activeSubPSL$ = this.store.select(CombinedSelectors.activeSubPSL);

    this.hasOptions$ = combineLatest([this.rawData$, this.activeSubPSL$]).pipe(
      map(([rows, activeSubPSL]) => this.toGroupsVM(rows ?? [], activeSubPSL, '').length > 0)
    );

    combineLatest([this.rawData$, this.activeSubPSL$, this.searchTerm$])
      .pipe(
        takeUntil(this.destroy$),
        map(([rows, activeSubPSL, searchTerm]) => this.toGroupsVM(rows ?? [], activeSubPSL, searchTerm))
      )
      .subscribe(vm => {
        this.buildOptionLookup();
        this.groupsVM = vm;
        const valid = new Set(vm.flatMap(g => g.options.map(o => o.id)));
        let changed = false;
        for (const id of [...this.selectedIdsSet]) {
          if (!valid.has(id)) {
            this.selectedIdsSet.delete(id);
            changed = true;
          }
        }
        if (changed) {
          this.persistSelection();
        }
        this.rebuildSelectedGroupsVm();
      });

    this.syncDraftSelection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchTerm$.complete();
  }

  trackGroup = (_: number, group: FeatureGroup) => group.id;
  trackOption = (_: number, option: FeatureOption) => option.id;

  isSelected(id: string | number): boolean {
    return this.selectedIdsSet.has(this.toKey(id));
  }
  private toKey(id: string | number): string {
    return String(id);
  }
  toggle(id: string | number): void {
    if (this.readonly) return; // Don't allow changes in readonly mode
    
    const key = this.toKey(id);
    const next = new Set(this.selectedIdsSet);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedIdsSet = next;

    this.rebuildSelectedGroupsVm();
    this.persistSelection();
  }

  clearAll(): void {
    if (this.readonly || this.disableClear || this.selectedIdsSet.size === 0) {
      return;
    }
    this.selectedIdsSet = new Set<string>();

    this.rebuildSelectedGroupsVm();
    this.persistSelection();
  }

  onSearch(term: string): void {
    const value = term ?? '';
    this.searchTerm = value;
    this.searchTerm$.next(value);
  }

  selectedGroups(): FeatureGroup[] {
    return this.selectedGroupsVm;
  }

  private toGroupsVM(
    rows: RawFeature[] | GroupResult[],
    activeSubPSL: string | null | undefined,
    searchTerm: string
  ): FeatureGroup[] {
    const grouped = this.ensureGroupResults(rows);
    const normalizedFilter = this.normalize(activeSubPSL);
    const shouldFilter = normalizedFilter !== '' && normalizedFilter !== 'na';
    const normalizedSearch = this.normalize(searchTerm);
    const hasSearch = normalizedSearch !== '';

    return grouped
      .map(result => {
        const items = this.flattenItems(result.items ?? []);
        const label = this.getGroupLabel(result, items);
        const filteredItems = items.filter(item => {
          if (shouldFilter && this.normalize((item as any)?.subPSL) !== normalizedFilter) {
            return false;
          }
          if (hasSearch) {
            const labelMatch = this.normalize(item.name).includes(normalizedSearch);
            const descriptionMatch = this.normalize(
              (item as any)?.Description ??
              (item as any)?.description ??
              (item as any)?.descriptor
            ).includes(normalizedSearch);
            if (!labelMatch && !descriptionMatch) {
              return false;
            }
          }
          return true;
        });
        const options: FeatureOption[] = filteredItems.map(item => ({
          id: String(item.value),
          label: item.name,
          raw: item
        }));
        return {
          id: label,
          label,
          options
        };
      })
      .filter(group => group.options.length > 0);
  }

  private ensureGroupResults(rows: RawFeature[] | GroupResult[]): GroupResult[] {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    if (this.isGroupResult(rows[0])) {
      return rows as GroupResult[];
    }

    return groupBy(rows as RawFeature[], this.groups) as GroupResult[];
  }

  private flattenItems(items: any[]): RawFeature[] {
    const stack = [...items];
    const flattened: RawFeature[] = [];
    while (stack.length) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (Array.isArray(current)) {
        stack.push(...current);
        continue;
      }
      if (this.isGroupResult(current)) {
        stack.push(...(current.items ?? []));
        continue;
      }
      flattened.push(current as RawFeature);
    }
    return flattened;
  }

  private getGroupLabel(group: GroupResult, items: RawFeature[]): string {
    if (group.value !== undefined && group.value !== null && group.value !== '') {
      return String(group.value);
    }

    const candidate = items[0];
    const label =
      (candidate as any)?.subcat ??
      (candidate as any)?.Subcat ??
      (candidate as any)?.subCategory ??
      (candidate as any)?.category;

    return label ? String(label) : 'Uncategorized';
  }

  private isGroupResult(candidate: any): candidate is GroupResult {
    return candidate && typeof candidate === 'object' && 'field' in candidate && 'items' in candidate;
  }

  private normalize(value: unknown): string {
    return value === null || value === undefined
      ? ''
      : String(value).trim().toLowerCase();
  }

  private buildOptionLookup(): void {
    const rows = this.store.selectSnapshot(OptionState.bitFeature) ?? [];
    const grouped = this.ensureGroupResults(rows);
    const lookup = new Map<string, RawFeature>();
    const groupLookup = new Map<string, string>();
    grouped.forEach(group => {
      const items = this.flattenItems(group.items ?? []);
      const label = this.getGroupLabel(group, items);
      items.forEach(item => {
        const key = String(item.value);
        lookup.set(key, item as RawFeature);
        groupLookup.set(key, label);
      });
    });
    this.optionLookup = lookup;
    this.optionGroupLookup = groupLookup;
    this.rebuildSelectedGroupsVm();
  }

  private persistSelection(): void {
    if (this.syncingFromStore) {
      return;
    }

    const ids = [...this.selectedIdsSet];
    this.selectionChange.emit(ids);

    const numericIds = ids
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id));

    const featureLabels = numericIds
      .map(id => this.optionLookup.get(String(id))?.name ?? '')
      .filter(label => !!label);

    const bitFeatures = featureLabels.join(', ');

    const draftGetter = this.store.selectSnapshot(RequestState.draftByIdFn);
    const state = this.store.selectSnapshot((root: { request: RequestStateModel }) => root.request);
    const draft =
      this.draftId != null
        ? draftGetter(this.draftId)
        : state.drafts['new'] ?? {};

    const bitPayload = new PatchORDBitDto({
      ...(draft?.bit ?? {}),
      bitFeatureIds: numericIds,
      orderId: this.draftId ?? (draft?.bit as PatchORDBitDto | undefined)?.orderId
    });

    const associations = this.createAssociations(numericIds);

    const requestPatch: any = {
      orderFeatureAssociations: associations,
      bit: bitPayload
    };

    this.store.dispatch([
      new RequestActions.PatchRequestDraft({
        draftId: this.draftId,
        changes: {
          bit: bitPayload,
          bitFeatureIds: numericIds
        }
      }),
      new RequestActions.PatchRequest(requestPatch)
    ]);
  }

  private createAssociations(ids: number[]): Partial<OrderFeatureAssociation>[] {
    const orderId = this.draftId ?? 0;
    return ids.map(id => ({
      bitFeatureID: id,
      orderID: orderId
    }));
  }

  private syncDraftSelection(): void {
    const draftGetter = this.store.selectSnapshot(RequestState.draftByIdFn);
    const state = this.store.selectSnapshot((root: { request: RequestStateModel }) => root.request);
    const draft =
      this.draftId != null
        ? draftGetter(this.draftId)
        : state.drafts['new'] ?? {};

    const featureIds = draft?.bitFeatureIds ?? [];
    this.syncingFromStore = true;
    this.applyIncomingSelection(featureIds);
    this.syncingFromStore = false;
    this.persistSelection();
  }

  private rebuildSelectedGroupsVm(): void {
    const groupedSelections = new Map<string, FeatureOption[]>();
    console.log('Rebuilding selected groups VM with IDs:', [...this.selectedIdsSet]);
    for (const id of this.selectedIdsSet) {
      const raw = this.optionLookup.get(id);
      if (!raw) {
        continue;
      }
      const label = this.optionGroupLookup.get(id) ?? 'Selected';
      const option: FeatureOption = {
        id,
        label: raw.name,
        raw
      };
      if (!groupedSelections.has(label)) {
        groupedSelections.set(label, []);
      }
      groupedSelections.get(label)!.push(option);
    }

    const orderedGroups: FeatureGroup[] = [];
    const seen = new Set<string>();

    this.groupsVM.forEach(group => {
      const opts = groupedSelections.get(group.label);
      if (opts && opts.length) {
        orderedGroups.push({
          id: group.id,
          label: group.label,
          options: [...opts]
        });
        seen.add(group.label);
      }
    });

    groupedSelections.forEach((options, label) => {
      if (seen.has(label) || options.length === 0) {
        return;
      }
      orderedGroups.push({
        id: label,
        label,
        options: [...options]
      });
    });
    console.log('Selected Groups VM rebuilt:', orderedGroups);
    console.log('Selected IDs Set:', [...this.selectedIdsSet]);
    this.selectedGroupsVm = orderedGroups;
  }

  private applyIncomingSelection(ids: Array<string | number>): void {
    const normalized = ids.map(String);
    const incomingSet = new Set(normalized);

    if (this.areSetsEqual(this.selectedIdsSet, incomingSet)) {
      return;
    }

    this.selectedIdsSet = incomingSet;
    this.rebuildSelectedGroupsVm();
    if (!this.syncingFromStore) {
      this.persistSelection();
    }
  }

  private areSetsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const value of a) {
      if (!b.has(value)) {
        return false;
      }
    }
    return true;
  }
}
